import { combine, createEvent, createStore, merge, sample } from 'effector';
import { persist } from 'effector-storage/local';
import { spread } from 'patronum';
import { GAME_FIELD_COLUMNS, GAME_FIELD_ROWS, GameCellVariant, gameModel } from 'entities/game';
import { receiveBet, receiveRefresh, receiveTurn, TURN_STATUSES } from 'shared/api';
import { createModalSwitcher } from 'shared/lib';

const DEFAULT_CELLS = Array.from<GameCellVariant[]>({ length: GAME_FIELD_ROWS }).fill(
	Array.from<GameCellVariant>({ length: GAME_FIELD_COLUMNS }).fill('default'),
);

const switcher = createModalSwitcher();

const $cells = createStore(DEFAULT_CELLS);

const $mines = createStore<number[][]>([]);

const $provablyFair = createStore('');

const $serverSeed = createStore('');

const $clientSeed = createStore('');

const $mask = combine(
	{
		serverSeed: $serverSeed,
		clientSeed: $clientSeed,
		mines: $mines,
	},
	({ mines, clientSeed, serverSeed }) =>
		serverSeed &&
		`client${clientSeed}server${serverSeed}mines${mines.map((level) => level.join(',')).join('.')}`,
);

const $clientSeedFieldReadOnly = gameModel.$turnStatus.map(
	(turnStatus) => turnStatus === TURN_STATUSES.bet,
);

const clientSeedChanged = createEvent<string>();

const betReceived = merge([receiveBet.success, receiveRefresh.success]);

const turnFinishReceived = receiveTurn.success.filter({
	fn: (turn) => turn.status !== TURN_STATUSES.bet,
});

sample({
	clock: clientSeedChanged,
	fn: (clientSeed) => clientSeed.replace(/\s/g, ''),
	target: gameModel.$clientSeed,
});

persist({
	store: gameModel.$clientSeed,
	key: 'leprecoins_stepball_client_seed',
});

sample({
	clock: betReceived,
	source: gameModel.$clientSeed,
	fn: (clientSeed, result) => ({
		clientSeed,
		provablyFair: result.provablyFair,
	}),
	target: [
		spread({
			clientSeed: $clientSeed,
			provablyFair: $provablyFair,
		}),
		$mines.reinit,
		$cells.reinit,
		$serverSeed.reinit,
	],
});

sample({
	clock: turnFinishReceived,
	fn: (turn) => ({
		serverSeed: turn.serverSeed ?? '',
		mines: turn.mines,
	}),
	target: spread({
		serverSeed: $serverSeed,
		mines: $mines,
	}),
});

sample({
	clock: turnFinishReceived,
	source: $cells,
	fn: (cells, { turnHistory, mines }) =>
		cells
			.map((row, ri) =>
				row.map((_, ci) => {
					if (mines[ri].includes(ci)) return 'mistake';

					if (turnHistory[ri] === ci) return 'ball';

					return 'ghost';
				}),
			)
			.reverse(),
	target: $cells,
});

export const fairnessModalModel = {
	switcher,
	$cells,
	$mines,
	$provablyFair,
	$serverSeed,
	$clientSeed,
	$mask,
	$clientSeedFieldReadOnly,
	clientSeedChanged,
};
